import React from 'react';
import Button from '@components/atoms/ui-kit/button.tsx';
import {
  editOptionsTranscript,
  funsTranscript
} from '@utils/constants/keyboard-shortcuts.ts';
import Modal from '@components/atoms/modals/modal.tsx';
import { useTranslation } from 'react-i18next';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { Info } from 'lucide-react';

const SharedTranscriptKeyboardInfo = () => {
  const { showInfo, setShowInfo } = useSharedTranscript();
  const { getColour } = useThemeContext();
  const { t } = useTranslation();

  const renderShortcutBadges = (shortcut: string) => {
    const shortcuts = shortcut.split(' ');
    return (
      <div className="inline-flex items-center gap-1 flex-wrap">
        {shortcuts.map((word, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <span
                className="text-xs font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                +
              </span>
            )}
            <kbd
              className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 text-xs font-medium rounded-2xl border"
              style={{
                backgroundColor: getColour('thirdLayer'),
                borderColor: getColour('border'),
                color: getColour('normalText')
              }}
            >
              {word}
            </kbd>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <Modal
      isOpen={showInfo}
      onClose={() => setShowInfo(false)}
      size="large"
      title={t('keyboard-shortcuts')}
      caption={t('list-of-keyboard-shortcuts')}
      footerActions={[
        <Button
          key="close"
          onClick={() => setShowInfo(false)}
          label={t('close-window')}
          size={'small'}
          variant={'smoke'}
        />
      ]}
    >
      <div className="space-y-4">
        {/* Info Banner */}
        <div
          className="flex items-start gap-3 p-3 rounded-2xl border"
          style={{
            backgroundColor: getColour('thirdLayer'),
            borderColor: getColour('border')
          }}
        >
          <Info
            className="w-4 h-4 flex-shrink-0 mt-0.5"
            style={{ color: getColour('activeText') }}
          />
          <p className="text-xs" style={{ color: getColour('inactiveText') }}>
            {t(
              'function-keys-info',
              'F5, F7, F8 work while editing text. On macOS, hold fn if function keys are mapped to system controls.'
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Playback Controls */}
          <div
            className="rounded-2xl border overflow-hidden"
            style={{ borderColor: getColour('border') }}
          >
            <div
              className="px-4 py-2 border-b"
              style={{
                backgroundColor: getColour('thirdLayer'),
                borderColor: getColour('border')
              }}
            >
              <h3
                className="text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('function')}
              </h3>
            </div>
            <div
              className="divide-y"
              style={{
                backgroundColor: getColour('secondLayer'),
                borderColor: getColour('border')
              }}
            >
              {funsTranscript.map((fun, index) => (
                <div
                  key={index}
                  className="px-4 py-3 flex items-start justify-between gap-3"
                  style={{
                    borderColor: getColour('border')
                  }}
                >
                  <span
                    className="text-sm font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {fun.action}
                  </span>
                  <div className="flex flex-col items-end gap-1.5">
                    {renderShortcutBadges(fun.shortcut)}
                    {fun.alternative && (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="text-xs"
                          style={{ color: getColour('inactiveText') }}
                        >
                          or
                        </span>
                        {renderShortcutBadges(fun.alternative)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Text Formatting */}
          <div
            className="rounded-2xl border overflow-hidden"
            style={{ borderColor: getColour('border') }}
          >
            <div
              className="px-4 py-2 border-b"
              style={{
                backgroundColor: getColour('thirdLayer'),
                borderColor: getColour('border')
              }}
            >
              <h3
                className="text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('edit-options')}
              </h3>
            </div>
            <div
              className="divide-y"
              style={{
                backgroundColor: getColour('secondLayer'),
                borderColor: getColour('border')
              }}
            >
              {editOptionsTranscript.map((editOption, index) => (
                <div
                  key={index}
                  className="px-4 py-3 flex items-center justify-between gap-3"
                  style={{
                    borderColor: getColour('border')
                  }}
                >
                  <span
                    className="text-sm font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {editOption.action}
                  </span>
                  {renderShortcutBadges(editOption.shortcut)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SharedTranscriptKeyboardInfo;
